// --------------------------------------------------------------
// Created On: 2021-09-01
// Author: Zachary Thomas
//
// Last Modified: 2026-05-28
// Modified By: Dimitra Weinstein
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment } from "react";
import SuccessStatusIcon from "../SuccessStatusIcon/SuccessStatusIcon";
import IconTooltip from "../IconTooltip/IconTooltip";
import { TOOLTIPS } from "../../constants/tooltips";

// Display an assets diagnosis status (wireless connection, modbus, sensors, etc...).
export default function DisplayDiagnosisStatus(props: Props): Component {
  // Returns a tooltip bullet array if there is a tooltip with a specific device type and validation type combination,
  // otherwise returns null.
  function getDeviceTypeTooltip(deviceType: string | null, validationType: string): TooltipBullet[] | null {
    if (deviceType !== null && deviceType in TOOLTIPS && validationType in TOOLTIPS[deviceType]) {
      const deviceTypeTooltips = TOOLTIPS[deviceType as keyof typeof TOOLTIPS];
      const deviceTypeAndValidationTypeSpecificTooltip = deviceTypeTooltips[validationType];
      return deviceTypeAndValidationTypeSpecificTooltip;
    } else {
      return null;
    }
  }

  const anAnalogSensorIsDisconnected = props.analogSensors.some((analogSensor) => !analogSensor.connected);
  return (props.deviceShouldReport || props.controllerShouldReport || props.analogSensors.length > 0) &&
    !props.loading ? (
    <div className="my-3 text-center">
      {props.deviceShouldReport && (
        <Fragment>
          {getDeviceTypeTooltip(props.deviceType, "deviceConnected") !== null && (
            <div>
              Monitoring device established wireless connection{" "}
              <IconTooltip
                id="diagnosis-status-device-connected"
                icon="info-circle"
                color="var(--info-tooltip)"
                message={getDeviceTypeTooltip(props.deviceType, "deviceConnected") || ""}
              />
              : <SuccessStatusIcon loading={false} successful={props.deviceConnected} />
            </div>
          )}
          {getDeviceTypeTooltip(props.deviceType, "latestFirmware") !== null && (
            <div>
              Monitoring device is using latest firmware{" "}
              <IconTooltip
                id="diagnosis-status-device-on-latest-firmware"
                icon="info-circle"
                color="var(--info-tooltip)"
                message={getDeviceTypeTooltip(props.deviceType, "latestFirmware") || ""}
              />
              : <SuccessStatusIcon loading={false} successful={props.deviceIsOnLatestFirmware} />
            </div>
          )}
        </Fragment>
      )}
      {props.controllerShouldReport && getDeviceTypeTooltip(props.deviceType, "controllerConnected") !== null && (
        <div>
          Monitoring device properly connected to controller{" "}
          <IconTooltip
            id="diagnosis-status-controller-connected"
            icon="info-circle"
            color="var(--info-tooltip)"
            message={getDeviceTypeTooltip(props.deviceType, "controllerConnected") || ""}
          />
          : <SuccessStatusIcon loading={false} successful={props.controllerConnected} />
        </div>
      )}
      {props.modbusSensorShouldReport && getDeviceTypeTooltip(props.deviceType, "modbusSensorsConnected") !== null && (
        <div>
          Monitoring device properly connected to Modbus sensor{" "}
          <IconTooltip
            id="diagnosis-status-modbus-sensor-connected"
            icon="info-circle"
            color="var(--info-tooltip)"
            message={getDeviceTypeTooltip(props.deviceType, "modbusSensorsConnected") || ""}
          />
          : <SuccessStatusIcon loading={false} successful={props.modbusSensorConnected} />
        </div>
      )}
      {props.analogSensors.length > 0 && getDeviceTypeTooltip(props.deviceType, "analogSensorsConnected") !== null && (
        <Fragment>
          <div>
            Analog sensors connected to monitoring device{" "}
            <IconTooltip
              id="diagnosis-status-analog-sensors-connected"
              icon="info-circle"
              color="var(--info-tooltip)"
              message={getDeviceTypeTooltip(props.deviceType, "analogSensorsConnected") || ""}
            />
            : <SuccessStatusIcon loading={false} successful={!anAnalogSensorIsDisconnected} />
          </div>
          <div className="mx-3">
            {props.analogSensors.map((analogSensor) => (
              <div key={analogSensor.position}>
                Analog sensor #{analogSensor.position} connected:{" "}
                <SuccessStatusIcon loading={false} successful={analogSensor.connected} />
              </div>
            ))}
          </div>
        </Fragment>
      )}
    </div>
  ) : (
    <div>Monitoring device does not support wireless diagnosis.</div>
  );
}

interface Props {
  loading: boolean;
  deviceType: string | null;
  deviceShouldReport: boolean;
  deviceConnected: boolean;
  deviceIsOnLatestFirmware: boolean;
  controllerShouldReport: boolean;
  controllerConnected: boolean;
  modbusSensorShouldReport: boolean;
  modbusSensorConnected: boolean;
  analogSensors: AnalogSensor[];
}

interface AnalogSensor {
  position: number;
  connected: boolean;
}

interface TooltipBullet {
  showBulletSymbol: boolean;
  title: string;
  description: string;
}
